// 問題の状態の移り変わり（docs/studio.md 6章）。サーバーはこの表にない移り変わりを断る。

export const QUESTION_STATES = [
  'ai_rejected',
  'candidate',
  'pending',
  'reviewing',
  'awaiting_stamps',
  'needs_decision',
  'on_hold',
  'rejected',
  'approved',
  'awaiting_audio',
  'audio_review',
  'ready',
  'published',
  'revising',
  'suspended',
  'trashed',
] as const;
export type QuestionState = (typeof QUESTION_STATES)[number];

export const QUESTION_STATE_LABEL: Record<QuestionState, string> = {
  ai_rejected: 'AI不合格',
  candidate: 'AIチェック待ち',
  pending: '確認待ち',
  reviewing: '確認中',
  awaiting_stamps: '承認待ち',
  needs_decision: '判断待ち',
  on_hold: '保留',
  rejected: '不合格',
  approved: '合格',
  awaiting_audio: '音声待ち',
  audio_review: '音声確認待ち',
  ready: '収録可',
  published: '公開中',
  revising: '修正中',
  suspended: '出題停止',
  trashed: 'ゴミ箱',
};

/**
 * 移り変わりの表（studio.md 6章）。
 * - candidate（AIチェック待ち）：手動で作問だけした候補。夜のファクトチェック・品質審査（S5）で確認待ちか AI不合格になる
 * - 問題の文や答えを直したとき：承認済み以降なら確認待ちに戻る（版が上がり、承認は押し直し）。公開中なら修正中
 */
export const QUESTION_TRANSITIONS: Record<QuestionState, readonly QuestionState[]> = {
  // 書き直し（T7、1回まで）で AI のチェックに戻る
  ai_rejected: ['candidate', 'trashed'],
  candidate: ['pending', 'ai_rejected', 'trashed'],
  pending: ['reviewing', 'trashed'],
  reviewing: ['pending', 'awaiting_stamps', 'approved', 'rejected', 'on_hold', 'needs_decision', 'revising', 'trashed'],
  awaiting_stamps: ['reviewing', 'pending', 'trashed'],
  // 差し戻し：直し案を採用したら AI のチェックからやり直す（candidate）、そのままでよければ確認待ちに戻す
  needs_decision: ['pending', 'candidate', 'approved', 'rejected', 'trashed'],
  on_hold: ['pending', 'trashed'],
  rejected: ['trashed'],
  approved: ['awaiting_audio', 'pending', 'trashed'],
  awaiting_audio: ['audio_review', 'approved', 'pending', 'trashed'],
  // 公開中の問題を直した版は、音声を承認すると公開中（新しい版）に戻る
  // 音声の確認で不採用（クイズごと外す。2026-10-09）
  audio_review: ['ready', 'published', 'awaiting_audio', 'pending', 'rejected', 'trashed'],
  ready: ['published', 'pending', 'trashed'],
  published: ['revising', 'suspended'],
  revising: ['reviewing', 'approved', 'awaiting_audio', 'audio_review', 'published', 'trashed'],
  // 出題停止から公開中に戻す（公開中の写しをそのまま使う）か、直して修正中へ
  suspended: ['revising', 'published', 'trashed'],
  // ゴミ箱（2026-10-08）：元に戻すと、入れる前の状態に戻る
  trashed: QUESTION_STATES.filter((s) => s !== 'published' && s !== 'trashed'),
};

/** ゴミ箱に入れられる状態：公開中（先に出題停止にする）とゴミ箱のほかすべて。使っているパックに入っている問題は入れられない（2026-10-09） */
export const TRASHABLE: readonly QuestionState[] = QUESTION_STATES.filter((s) => s !== 'published' && s !== 'trashed');

export function canTransition(from: QuestionState, to: QuestionState) {
  return QUESTION_TRANSITIONS[from].includes(to);
}

/** 人の確認より前（まだ内容が確定していない）。確認待ち・確認中・承認待ち・判断待ち・保留 */
export const BEFORE_APPROVAL: readonly QuestionState[] = ['candidate', 'pending', 'reviewing', 'awaiting_stamps', 'needs_decision', 'on_hold'];
/** 内容が確定した（承認済み以降）。作問ガイド G12 の重複で、ほかの問題を止める相手になる */
export const APPROVED_OR_LATER: readonly QuestionState[] = ['approved', 'awaiting_audio', 'audio_review', 'ready', 'published', 'revising', 'suspended'];
/** 使わない問題（重複の相手にもしない） */
export const DISCARDED: readonly QuestionState[] = ['ai_rejected', 'rejected', 'trashed'];
